import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { api, fileUrl, type FileRef, type LinkPreview, type Message } from "../api";
import { useShallow } from "zustand/shallow";
import { discard, go, retry, upsertMessage, useStore, type Pending } from "../store";
import { Rich, plain, timeLabel } from "../text";
import { Avatar, isOwner, sizeLabel, toast, useMe } from "./ui";
import { isDesktop } from "../desktop";
import { Picker, QUICK, ReactionRow, toggle } from "./Reactions";
import { Icon } from "./icons";

export type Item = { kind: "msg"; m: Message } | { kind: "pending"; p: Pending };

interface Props {
  item: Item;
  cont: boolean;
  inThread?: boolean;
  /** A task reply: no tools. */
  bare?: boolean;
  focus?: boolean;
  onMakeTask?: (m: Message, el: HTMLElement) => void;
}

export function Files({ files }: { files: FileRef[] }) {
  const [big, setBig] = useState<FileRef | null>(null);
  if (!files?.length) return null;
  // In the app, files save to Downloads; in a browser, PDFs open in a tab.
  const desk = isDesktop();
  return (
    <div className="files">
      {files.map((f) => f.mime.startsWith("image/")
        ? <img key={f.id} src={fileUrl(f.id)} alt={f.name} loading="lazy" onClick={() => setBig(f)} />
        : desk || !f.mime.includes("pdf")
          ? <a key={f.id} className="fchip" href={fileUrl(f.id, true)} download={f.name}>📎 {f.name} <small>{sizeLabel(f.size)}</small></a>
          : <a key={f.id} className="fchip" href={fileUrl(f.id)} target="_blank" rel="noopener">📎 {f.name} <small>{sizeLabel(f.size)}</small></a>)}
      {big && (
        <div className="lightbox" onClick={(e) => e.target === e.currentTarget && setBig(null)} onKeyDown={(e) => e.key === "Escape" && setBig(null)} tabIndex={-1} ref={(el) => el?.focus()}>
          <img src={fileUrl(big.id)} alt={big.name} />
          <div className="bar">{big.name}<a href={fileUrl(big.id, true)} download={big.name}>Download</a></div>
        </div>
      )}
    </div>
  );
}

export function MessageItem({ item, cont, inThread, bare, focus, onMakeTask }: Props) {
  const me = useMe();
  const users = useStore((s) => s.users);
  const root = useStore((s) => (item.kind === "msg" && item.m.parentId ? s.byId[item.m.parentId] : undefined));
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [picking, setPicking] = useState(false);
  const [more, setMore] = useState(false);
  const names = useStore(useShallow((s) => Object.values(s.users).map((u) => u.name)));
  const editingId = useStore((s) => s.editingId);
  const pinned = useStore((s) => (item.kind === "msg" ? s.pins.find((p) => p.messageId === item.m.id) : undefined));
  const saved = useStore((s) => (item.kind === "msg" ? s.saved[item.m.id] : undefined));
  // ↑ in an empty message box opens your last message for editing.
  useEffect(() => {
    if (item.kind === "msg" && editingId === item.m.id) { setDraft(item.m.body); setEditing(true); useStore.setState({ editingId: null }); }
  }, [editingId]);

  const size = inThread ? 28 : 32;
  if (item.kind === "pending") {
    const p = item.p;
    return (
      <div className={`msg${cont ? " cont" : ""}`}>
        <div className="gut">{!cont && <Avatar user={me} size={size} />}</div>
        <div className="mb">
          {!cont && <div className="who"><b>{me.name}</b></div>}
          <div className="tx" style={{ opacity: 0.6 }}><Rich text={p.body} /><Files files={p.files} /></div>
          <div className={`pend${p.failed ? " bad" : ""}`}>
            {p.failed ?? "Sending…"}
            {p.failed && <><button onClick={() => retry(p.clientId)}>Retry</button><button onClick={() => discard(p.clientId)}>Remove</button></>}
          </div>
        </div>
      </div>
    );
  }

  const m = item.m;
  const mine = m.userId === me.id;
  const author = users[m.userId];
  const deleted = !!m.deletedAt;
  const showVia = !inThread && m.parentId && m.alsoInChat;
  const head = !cont || !!showVia || !!pinned;
  const mentionsMe = m.mentions?.includes(me.id) && !mine;

  const saveEdit = async () => {
    const body = draft.trim();
    setEditing(false);
    if (!body || body === m.body) return;
    try { upsertMessage(await api.edit(m.id, body)); } catch (e: any) { toast(e.message || "Couldn't edit"); }
  };

  return (
    <div className={`msg${head ? "" : " cont"}${focus ? " focus" : ""}${mentionsMe ? " men" : ""}${pinned ? " pinned" : ""}`} id={`m-${m.id}`}>
      <div className="gut">{head ? <Avatar user={author} size={size} /> : <span className="t">{timeLabel(m.createdAt)}</span>}</div>
      <div className="mb">
        {(pinned || saved) && (
          <div className="flags">
            {pinned && <span className="fl pinf"><Icon n="pin" s={12} />Pinned by {pinned.pinnedBy === me.id ? "you" : users[pinned.pinnedBy]?.name}</span>}
            {saved && <span className="fl savf"><Icon n={saved.remindAt && !saved.reminded ? "clock" : "later"} s={12} />{saved.remindAt && !saved.reminded ? `Reminder ${new Date(saved.remindAt).toLocaleString("en-GB", { weekday: "short", hour: "2-digit", minute: "2-digit" })}` : "Saved for later"}</span>}
          </div>
        )}
        {head && <div className="who"><b>{author?.name ?? "Someone"}</b><span>{timeLabel(m.createdAt)}</span></div>}
        {showVia && (
          <div className="via"><button onClick={() => go({ v: "room", id: m.roomId, thread: m.parentId! })}>
            replied to a thread: {root ? plain(root.body).slice(0, 70) || "a message" : "a message"}
          </button></div>
        )}
        {editing ? (
          <div className="box" style={{ marginTop: 4 }}>
            <div className="in"><textarea autoFocus rows={Math.min(8, draft.split("\n").length)} value={draft} onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); saveEdit(); } if (e.key === "Escape") setEditing(false); }} /></div>
            <div className="opt">Enter to save · Esc to cancel</div>
          </div>
        ) : (
          <div className="tx">
            {deleted ? <span className="del">Message deleted</span> : <Rich text={m.body} names={names} me={me.name} />}
            {m.editedAt && !deleted && <span className="ed">(edited)</span>}
            {!deleted && <Files files={m.files} />}
          </div>
        )}
        {!deleted && m.preview && <Preview p={m.preview} />}
        {!deleted && <ReactionRow m={m} />}
        {!inThread && m.replyCount > 0 && !m.parentId && <Replies m={m} />}
      </div>
      {!deleted && !editing && !bare && (
        <div className={`tools${picking || more ? " keep" : ""}`}>
          {QUICK.slice(0, 3).map((e) => <button key={e} className="q" title={`React ${e}`} onClick={() => toggle(m, e, me.id)}>{e}</button>)}
          <button className="tb" title="Add a reaction" onClick={() => setPicking((p) => !p)}><Icon n="smile" s={14} />React</button>
          <span className="sep" />
          {!inThread && !m.parentId && <button className="tb" onClick={() => go({ v: "room", id: m.roomId, thread: m.id })}><Icon n="reply" s={14} />Reply</button>}
          {isOwner(me) && onMakeTask && <button className="tb" onClick={(e) => onMakeTask(m, (e.currentTarget.closest(".msg") as HTMLElement))}><Icon n="task" s={14} />Task</button>}
          <button className="tb" title={saved ? "Remove from Later" : "Save for later"} onClick={() => api.save(m.id, null, !saved).then((l) => { useStore.setState({ saved: Object.fromEntries(l.map((x) => [x.messageId, x])) }); toast(saved ? "Removed from Later" : "Saved to Later"); }).catch(() => toast("Didn't work"))}>
            <Icon n="later" s={14} />{saved ? "Saved" : "Save"}</button>
          <button className="tb" title="More" onClick={() => setMore((o) => !o)}><Icon n="more" s={14} />More</button>
          {picking && <Picker m={m} onClose={() => setPicking(false)} />}
          {more && <MoreMenu m={m} mine={mine} saved={!!saved} pinned={!!pinned} inThread={!!inThread}
            onEdit={() => { setDraft(m.body); setEditing(true); }} onClose={() => setMore(false)} />}
        </div>
      )}
    </div>
  );
}

function Preview({ p }: { p: LinkPreview }) {
  const [img, setImg] = useState(true);
  return (
    <a className="lp" href={p.url} target="_blank" rel="noopener noreferrer">
      <span className="lpt">
        <small>{p.site}</small>
        <b>{p.title}</b>
        {p.description && <span>{p.description.slice(0, 220)}</span>}
      </span>
      {p.image && img && <img src={p.image} alt="" referrerPolicy="no-referrer" loading="lazy" onError={() => setImg(false)} />}
    </a>
  );
}

const inMinutes = (n: number) => new Date(Date.now() + n * 60000);
function tomorrowNine() { const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(9, 0, 0, 0); return d; }

/** ⋯ on a message: save and remind, pin, copy link, mark unread, follow the thread, edit, delete. */
function MoreMenu({ m, mine, saved, pinned, inThread, onEdit, onClose }: { m: Message; mine: boolean; saved: boolean; pinned: boolean; inThread: boolean; onEdit: () => void; onClose: () => void }) {
  const box = useRef<HTMLDivElement>(null);
  const following = useStore((s) => !!s.follows[m.parentId ?? m.id]);
  const [sure, setSure] = useState(false);
  // Float above everything and flip upwards near the bottom, so the chat's scroll never cuts it off.
  useLayoutEffect(() => {
    const el = box.current, anchor = el?.parentElement;
    if (!el || !anchor) return;
    const a = anchor.getBoundingClientRect(), h = el.offsetHeight, w = el.offsetWidth;
    const below = a.bottom + 6 + h < innerHeight - 8;
    Object.assign(el.style, { position: "fixed", right: "auto", left: `${Math.max(8, Math.min(a.right - w, innerWidth - w - 8))}px`, top: `${below ? a.bottom + 6 : Math.max(8, a.top - h - 6)}px` });
  }, []);
  useEffect(() => {
    const c = (e: MouseEvent) => !box.current?.contains(e.target as Node) && onClose();
    const k = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    setTimeout(() => addEventListener("mousedown", c)); addEventListener("keydown", k);
    return () => { removeEventListener("mousedown", c); removeEventListener("keydown", k); };
  }, [onClose]);
  const act = (f: () => Promise<unknown>, done?: string) => async () => {
    onClose();
    try { await f(); if (done) toast(done); } catch (e: any) { toast(e.message || "Didn't work"); }
  };
  const remind = (at: Date) => act(async () => useStore.setState({ saved: Object.fromEntries((await api.save(m.id, at.toISOString())).map((x) => [x.messageId, x])) }),
    `I'll remind you ${at.toLocaleString("en-GB", { weekday: "short", hour: "2-digit", minute: "2-digit" })}`);
  const link = `${location.origin}/c/${m.roomId}${m.parentId ? `/thread/${m.parentId}` : ""}?m=${m.id}`;
  return (
    <div className="menu" ref={box}>
      <button className="mi" onClick={act(async () => useStore.setState({ saved: Object.fromEntries((await api.save(m.id, null, !saved)).map((x) => [x.messageId, x])) }), saved ? "Removed from Later" : "Saved to Later")}><Icon n="later" /><span>{saved ? "Remove from Later" : "Save for later"}</span></button>
      <div className="sub"><Icon n="clock" /><span>Remind me</span>
        <button onClick={remind(inMinutes(20))}>in 20 min</button><button onClick={remind(inMinutes(60))}>in 1 hour</button><button onClick={remind(tomorrowNine())}>tomorrow 9:00</button>
      </div>
      <hr />
      <button className="mi" onClick={act(() => api.pin(m.id, !pinned).then((p) => useStore.setState((s) => ({ pins: [...s.pins.filter((x) => x.roomId !== m.roomId), ...p] }))), pinned ? "Unpinned" : "Pinned to the chat")}><Icon n="pin" /><span>{pinned ? "Unpin" : "Pin to this chat"}</span></button>
      <button className="mi" onClick={act(() => navigator.clipboard.writeText(link), "Link copied")}><Icon n="link" /><span>Copy link</span></button>
      <button className="mi" onClick={act(async () => {
        // Hold first, so the open chat doesn't mark it read again the moment the server answers.
        if (!m.parentId) useStore.setState((s) => ({ holdUnread: { ...s.holdUnread, [m.roomId]: true } }));
        await api.unread(m.id);
      }, "Marked unread")}><Icon n="unread" /><span>Mark unread from here</span></button>
      {(!m.parentId || inThread) && (m.replyCount > 0 || m.parentId) && <button className="mi" onClick={act(() => api.follow(m.parentId ?? m.id, !following), following ? "You won't hear about new replies" : "You'll hear about new replies")}><Icon n="follow" /><span>{following ? "Stop following thread" : "Follow thread"}</span></button>}
      {mine && <><hr /><button className="mi" onClick={() => { onClose(); onEdit(); }}><Icon n="edit" /><span>Edit</span><kbd>↑</kbd></button>
        <button className="mi bad" onClick={sure ? act(() => api.del(m.id)) : () => setSure(true)}><Icon n="trash" /><span>{sure ? "Click again to delete" : "Delete"}</span></button></>}
    </div>
  );
}

/** "3 replies": the faces of who replied (from the loaded thread, else the author), then when. */
function Replies({ m }: { m: Message }) {
  const users = useStore((s) => s.users);
  const thread = useStore((s) => s.threads[m.id]);
  const who = [...new Set((thread ?? []).map((r) => r.userId))].slice(0, 4);
  return (
    <button className="reps" onClick={() => go({ v: "room", id: m.roomId, thread: m.id })}>
      {who.map((u) => <Avatar key={u} user={users[u]} size={18} />)}
      {m.replyCount} {m.replyCount === 1 ? "reply" : "replies"}{m.lastReplyAt && <span>last {timeLabel(m.lastReplyAt)}</span>}
    </button>
  );
}

/** Same person within five minutes, no day break: no second name line. */
export function continues(prev: Item | undefined, cur: Item, meId: number) {
  if (!prev) return false;
  const who = (i: Item) => (i.kind === "pending" ? meId : i.m.userId);
  const at = (i: Item) => new Date(i.kind === "pending" ? i.p.createdAt : i.m.createdAt).getTime();
  return who(prev) === who(cur) && at(cur) - at(prev) < 5 * 60000 && new Date(at(prev)).toDateString() === new Date(at(cur)).toDateString();
}
